import React, { useContext, useEffect, useState, memo } from "react";
import { styled } from "@mui/material/styles";
import {
  Datagrid,
  TextField,
  Show as BaseShow,
  ArrayField,
  DateField,
  Tab,
  TabbedShowLayout,
  Error,
  Loading,
  FunctionField,
  Button,
  NumberField,
  useUpdate,
  useGetList,
  useNotify,
  useRedirect,
  useShowController,
  BooleanField,
  NumberInput,
  Edit,
  ArrayInput,
  SimpleForm,
  SimpleFormIterator,
  FormDataConsumer,
  TextInput,
  useEditController,
  useEditContext,
  useRecordContext,
  SaveButton,
  SelectInput,
  useDataProvider,
  useRefresh,
} from "react-admin";
import { toHoursAndMinutes } from "../util";
import {
  MenuItem,
  Button as MuiButton,
  TextField as MuiTextField,
  FormControl,
  Box,
  Input,
  useFormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
} from "@mui/material";
import moment from "moment";
import Grid from "@mui/material/Grid";
import GoogleMapReact from "google-map-react";

import RoomIcon from "@mui/icons-material/Room";
import { Chat } from "../Chat";
import { useSearchParams } from "react-router-dom";

const paymentMethods = [
  { id: "Efectivo", name: "Efectivo" },
  { id: "Tarjeta", name: "Tarjeta" },
  { id: "QR", name: "QR" },
  { id: "Tigo Money", name: "Tigo Money" },
  { id: "Cortesia", name: "Cortesia" },
  { id: "Gift Card", name: "Gift Card" },
];

const status = (record) => {
  if (record.status === "Pendiente de cotizacion") {
    return [
      { id: "Pendiente de cotizacion", name: "Pendiente de cotización" },
      { id: "Confirmado", name: "Enviar cotización y confirmar" },
      { id: "Cancelada por el proveedor", name: "Cancelado" },
    ];
  }
  if (record.status === "Cotizacion enviada") {
    return [{ id: "Cotizacion enviada", name: "Cotización enviada" }];
  }
  if (record.status === "Pendiente") {
    return [
      { id: "Pendiente", name: "Pendiente" },
      { id: "Confirmado", name: "Confirmar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "Confirmado") {
    return [
      { id: "Confirmado", name: "Confirmado" },
      { id: "En proceso", name: "En proceso" },
      { id: "Finalizado", name: "Finalizar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "En proceso") {
    return [
      { id: "En proceso", name: "En proceso" },
      { id: "Finalizado", name: "Finalizar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "Finalizado") {
    return [{ id: "Finalizado", name: "Finalizado" }];
  }
  if (record.status === "Cancelada por el proveedor") {
    return [{ id: "Cancelada por el proveedor", name: "Cancelado por el proveedor" }];
  }
  if (record.status === "Cancelada por el cliente") {
    return [{ id: "Cancelada por el cliente", name: "Cancelado por el cliente" }];
  }
};

const PREFIX = "UseEmployee";

const classes = {
  inlineBlock: `${PREFIX}-inlineBlock`,
};

const StyledBaseShow = styled(Edit)({
  [`& .${classes.inlineBlock}`]: {
    display: "inline-flex",
    marginRight: "1rem",
  },
});

const PaymentStatusField = memo(({ record }) => {
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const handleChange = async (newStatus) => {
    setLoading(true);
    const totalPrice =
      newStatus === "Cortesia" ? Number(0) : record.serviceCart?.reduce((a, b) => a + (b.price || 0), 0) || 0;

    try {
      await dataProvider.update("booking", {
        id: record._id,
        data: { totalPrice: totalPrice },
        meta: { customAction: "updateTotalPrice" },
      });

      await dataProvider.update("booking", {
        id: record._id,
        data: { paymentMethod: newStatus },
        meta: { customAction: "updatePaymentMethod" },
      });
      refresh();
      notify("Actualizado correctamente", { type: "success" });

      // Redirigir al calendario después de cambiar el método de pago
      setTimeout(() => {
        redirect("/schedule");
      }, 1000); // Esperar 1 segundo para que el usuario vea la notificación
    } catch (error) {
      notify("Error al actualizar", { type: "error" });
    } finally {
      setLoading(false);
    }
  };
  return (
    <SimpleForm toolbar={false}>
      <SelectInput
        source="paymentInfo.paymentMethod"
        label="Método de Pago"
        choices={paymentMethods || []}
        disabled={loading}
        onChange={(e) => {
          return handleChange(e.target.value);
        }}
        style={{ margin: 0, padding: 0 }}
      />
    </SimpleForm>
  );
});

const BookingStatus = memo(({ record }) => {
  const [loading, setLoading] = useState(false);
  const [choices, setChoices] = useState(status(record));
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();

  const handleChange = async (newStatus) => {
    setLoading(true);
    try {
      await dataProvider.update("booking", {
        id: record._id,
        data: { status: newStatus, previousStatus: record.status },
        meta: { customAction: "updateStatus" },
      });

      setChoices(status({ ...record, status: newStatus }));

      notify("Actualizado correctamente", { type: "success" });

      // Redirigir al calendario después de cambiar el estado
      setTimeout(() => {
        redirect("/schedule");
      }, 1000); // Esperar 1 segundo para que el usuario vea la notificación
    } catch (error) {
      notify("Error al actualizar", { type: "error" });
    } finally {
      setLoading(false);
    }
  };
  return (
    <SimpleForm toolbar={false}>
      <SelectInput
        source="status"
        label="Estado de Reserva"
        choices={choices || []}
        disabled={loading}
        onChange={(e) => {
          return handleChange(e.target.value);
        }}
        style={{ margin: 0, padding: 0 }}
        sx={{ padding: 0, margin: 0 }}
      />
    </SimpleForm>
  );
});

export const UseEmployee = (props) => {
  const { data, loading, error } = useGetList("employee", {
    filter: {
      startDate: moment(props.record?.bookingDate).format(),
      endDate: moment(props.record?.bookingDate)
        .add(props.record?.totalEstimatedWorkMinutes - 1, "minutes")
        .format(),
      idProv: props.record?.provider._id,
      forAssign: true,
      isInBranch: props.record?.isInBranch,
      branch: props.record?.branch?._id,
      serviceCart: props.record?.serviceCart.map((e) => e.service?._id),
    },
  });
  if (loading) return <Loading />;
  if (error) return <Error />;
  if (!data) return null;
  // console.log("record: ", props.record);
  // console.log("dataEmployee: ", data);
  return (
    <FormControl style={{ minWidth: 200 }}>
      {/* <InputLabel id="employee">Empleado Asignado</InputLabel> */}
      <MuiTextField
        className="MuiInputBase-root MuiFilledInput-root MuiFilledInput-underline MuiInputBase-formControl MuiInputBase-marginDense MuiFilledInput-marginDense"
        autoWidth
        select
        onChange={(e) => {
          props.setEmployee(e.target.value);
        }}
        labelId="employee"
        children={data
          ?.filter((e) => e.state)
          .map((e) => {
            return <MenuItem value={e}>{e.first_name + " " + e.last_name}</MenuItem>;
          })}
        label="Empleado Asignado"
      />
    </FormControl>
  );
};

export const Show = ({ ...props }) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const [employee, setEmployee] = useState(null);
  const { data, record } = useEditController(props);
  const [update, { loading }] = useUpdate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDisabled, setIsDisabled] = useState(true);

  const confirmBooking = (withEmployee = true) =>
    update(
      "booking",
      {
        id: record._id,
        data: {
          employee: withEmployee
            ? {
                _id: employee._id,
                fullName: employee.first_name + " " + employee.last_name,
                phone: employee.phone,
                photoURL: employee.picture,
                pushToken: employee.pushToken,
                CI: employee.CI_NIT,
                location: [],
              }
            : null,
        },
        meta: { customAction: "confirm" },
      },
      {
        onSuccess: (data) => {
          const redirectParam = searchParams.get("redirect")
            ? searchParams.get("redirect") + "?date=" + data.bookingDate
            : null;
          redirect(redirectParam ? `/${redirectParam}` : "/booking");
          notify("Empleado asignado con éxito");
        },
        onFailure: (error) => notify(`Error al asignar empleado: ${error}`, "warning"),
      }
    );

  const assignEmployee = (e) => {
    e.preventDefault();
    if (employee) {
      return confirmBooking();
    } else {
      return notify("Asignar al empleado.", "warning");
    }
  };

  const confirmWithoutEmployee = (e) => {
    e.preventDefault();
    return confirmBooking(false);
  };

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // const cancel = () =>
  //   update(
  //     "booking",
  //     {
  //       id: record._id,
  //       data: {},
  //       meta: { customAction: "toCancelledByProvider" },
  //     },
  //     {
  //       onSuccess: (data) => {
  //         const redirectParam = searchParams.get("redirect")
  //           ? searchParams.get("redirect") + "?date=" + data.bookingDate
  //           : null;
  //         redirect(redirectParam ? `/${redirectParam}` : "/booking");
  //         notify("Reserva cancelada con éxito");
  //       },
  //       onFailure: (error) => notify(`Error al cancelar reserva: ${error}`, "warning"),
  //     }
  //   );

  const cancel = () =>
    update(
      "booking",
      {
        id: record._id,
        data: { cancelReason: cancelReason.trim() },
        meta: { customAction: "toCancelledByProvider" },
      },
      {
        onSuccess: (data) => {
          const redirectParam = searchParams.get("redirect")
            ? searchParams.get("redirect") + "?date=" + data.bookingDate
            : null;
          redirect(redirectParam ? `/${redirectParam}` : "/booking");
          notify("Reserva cancelada con éxito");
          setOpenCancelModal(false);
          setCancelReason("");
        },
        onFailure: (error) => {
          notify(`Error al cancelar reserva: ${error}`, "warning");
          setOpenCancelModal(false);
        },
      }
    );

  const handleOpenCancelModal = () => setOpenCancelModal(true);
  const handleCloseCancelModal = () => {
    setOpenCancelModal(false);
    setCancelReason("");
  };

  const submitCancel = (e) => {
    e.preventDefault();
    cancel();
  };

  const finish = () =>
    update(
      "booking",
      {
        id: record._id,
        data: {},
        meta: { customAction: "toFinishedAndPaid" },
      },
      {
        onSuccess: (data) => {
          const redirectParam = searchParams.get("redirect")
            ? searchParams.get("redirect") + "?date=" + data.bookingDate
            : null;
          redirect(redirectParam ? `/${redirectParam}` : "/booking");
          notify("servicio finalizado con éxito");
        },
        onFailure: (error) => notify(`Error al finalizar servicio: ${error}`, "warning"),
      }
    );

  const submitFinish = (e) => {
    e.preventDefault();
    finish();
  };

  const start = () =>
    update(
      "booking",
      {
        id: record._id,
        data: {},
        meta: { customAction: "toProcessing" },
      },
      {
        onSuccess: (data) => {
          const redirectParam = searchParams.get("redirect")
            ? searchParams.get("redirect") + "?date=" + data.bookingDate
            : null;
          redirect(redirectParam ? `/${redirectParam}` : "/booking");
          notify("servicio iniciado con éxito");
        },
        onFailure: (error) => notify(`Error al iniciar servicio: ${error}`, "warning"),
      }
    );

  const submitStart = (e) => {
    e.preventDefault();
    start();
  };

  const pay = () =>
    update(
      "booking",
      {
        id: record._id,
        data: {},
        meta: { customAction: "toPaid" },
      },
      {
        onSuccess: (data) => {
          const redirectParam = searchParams.get("redirect")
            ? searchParams.get("redirect") + "?date=" + data.bookingDate
            : null;
          redirect(redirectParam ? `/${redirectParam}` : "/booking");
          notify("servicio pagado con éxito");
        },
        onFailure: (error) => notify(`Error al pagar servicio: ${error}`, "warning"),
      }
    );

  const submitPaid = (e) => {
    e.preventDefault();
    pay();
  };

  const quote = (data) =>
    update(
      "booking",
      {
        id: record._id,
        data: {
          serviceCart: data.serviceCart,
          employee: data.employee,
        },
        meta: { customAction: "toQuoteSentAndConfirm" },
      },
      {
        onSuccess: (data) => {
          const redirectParam = searchParams.get("redirect")
            ? searchParams.get("redirect") + "?date=" + data.bookingDate
            : null;
          redirect(redirectParam ? `/${redirectParam}` : "/booking");
          notify("servicio cotizado con éxito");
        },
        onFailure: (error) => notify(`Error al cotizar servicio: ${error}`, "warning"),
      }
    );

  const submitQuote = (data) => {
    data.serviceCart = data.serviceCart.map((item) => {
      item.price = item.quantity * item.service.unitPrice;
      return item;
    });
    quote(data);
  };

  return (
    //  <StyledBaseShow {...props}>
    <Edit {...props}>
      <SimpleForm
        onSubmit={submitQuote}
        toolbar={
          record && record.status === "Pendiente de cotizacion" ? (
            <SaveButton
              // variant="contained"
              // color="primary"
              // size="small"
              // type="button"
              label="Enviar cotización y confirmar"
              alwaysEnable

              // onClick={submitQuote}
            >
              {/* Enviar cotización */}
            </SaveButton>
          ) : (
            <></>
          )
        }
        // style={{ alignItems: "normal" }}
        // className=""
      >
        <TabbedShowLayout>
          <Tab label="Información de la Reserva">
            <TextField label="Estado de la reserva:"></TextField>
            {record && (
              <div onClick={(e) => e.stopPropagation()}>
                <BookingStatus record={record} />
              </div>
            )}
            <DateField showTime source="bookingDate" label="Fecha de Inicio" locales="es-ES" />

            {record &&
              record.serviceCart &&
              (record?.status === "Pendiente de cotizacion" ? (
                <ArrayInput source="serviceCart" label="Servicios Adquiridos">
                  <SimpleFormIterator disableReordering inline disableAdd disableClear disableRemove>
                    <TextInput source="service.name" label="Nombre" disabled />
                    <NumberInput source="quantity" label="Cantidad" disabled />

                    {/* {record.serviceCart.some(
                      ({ service }) => service.variablePrice
                    ) ? ( */}
                    <NumberInput source="service.unitPrice" label="Precio Unitario" min={0} />
                    {/* ) : (
                      <NumberField
                        source="service.unitPrice"
                        label="Precio Unitario"
                      />
                    )} */}

                    <FormDataConsumer>
                      {({ formData, scopedFormData, getSource }) => {
                        return (
                          <NumberInput
                            source="price"
                            label="Subtotal (Bs)"
                            disabled
                            format={() => scopedFormData.quantity * scopedFormData.service.unitPrice}
                          />
                        );
                      }}
                    </FormDataConsumer>

                    <TextInput
                      label="Tiempo Estimado Unitario"
                      textAlign="center"
                      disabled
                      source="service.unitEstimatedWorkMinutes"
                      format={toHoursAndMinutes}
                    />
                    <TextInput
                      label="Tiempo Estimado Subtotal"
                      textAlign="center"
                      disabled
                      source="estimatedWorkMinutes"
                      format={toHoursAndMinutes}
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              ) : (
                <ArrayField source="serviceCart" label="Servicios Adquiridos">
                  <Datagrid bulkActionButtons={false}>
                    <TextField source="service.name" label="Nombre" />
                    <NumberField source="quantity" label="Cantidad" />
                    <NumberField source="service.unitPrice" label="Precio Unitario" />
                    <NumberField source="price" label="Subtotal (Bs)" />
                    <FunctionField
                      label="Tiempo Estimado Unitario"
                      textAlign="center"
                      render={({ service }) => toHoursAndMinutes(service.unitEstimatedWorkMinutes)}
                    />
                    <FunctionField
                      label="Tiempo Estimado Subtotal"
                      textAlign="center"
                      render={({ estimatedWorkMinutes }) => toHoursAndMinutes(estimatedWorkMinutes)}
                    />
                  </Datagrid>
                </ArrayField>
              ))}
            {record?.status === "Pendiente de cotizacion" ? (
              <FormDataConsumer>
                {({ formData, scopedFormData, getSource }) => {
                  return (
                    <NumberInput
                      source="paymentInfo.totalPrice"
                      label="Precio Total (Bs)"
                      disabled
                      format={(v) => formData.paymentInfo?.totalPrice || 0}
                      parse={(v) => formData.paymentInfo?.totalPrice || 0}
                    />
                  );
                }}
              </FormDataConsumer>
            ) : (
              <TextField source="paymentInfo.totalPrice" label="Precio Total (Bs)" />
            )}
            {record?.paymentInfo?.status !== "Pagado" && (
              <FormDataConsumer>
                {({ formData, scopedFormData, getSource }) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        margin: "10px 0px",
                      }}
                    >
                      <p>{`Total pagos parciales (Bs): ${record?.paymentInfo?.partialPayment}`}</p>
                      <NumberInput
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          const amount = Number(record?.paymentInfo?.partialPayment || 0);
                          const totalPrice = Number(record?.paymentInfo?.totalPrice || 0);
                          const pay = Number(value + amount);
                          const isInvalid = pay <= 0 || pay > totalPrice || value === 0;
                          setIsDisabled(isInvalid);
                        }}
                        source="partialPay"
                        label="Pago Parcial"
                        min={0}
                        sx={{ width: "150px" }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isDisabled}
                        onClick={() => {
                          const amount = formData?.partialPay || 0;
                          const totalAmount = Number(amount) + Number(record?.paymentInfo?.partialPayment || 0);
                          const totalPrice = Number(record?.paymentInfo?.totalprice);

                          if (totalAmount > totalPrice) return;

                          update(
                            "booking",
                            {
                              id: record._id,
                              data: { partialPayment: totalAmount },
                              meta: { customAction: "updatePartialPayment" },
                            },
                            {
                              onSuccess: (data) => {
                                const redirectParam = searchParams.get("redirect")
                                  ? searchParams.get("redirect") + "?date=" + data.bookingDate
                                  : null;
                                redirect(redirectParam ? `/${redirectParam}` : "/booking");
                                notify("servicio pagado con éxito");
                              },
                              onFailure: (error) => notify(`Error al pagar servicio: ${error}`, "warning"),
                            }
                          );
                        }}
                        size="small"
                        sx={{ width: "150px" }}
                      >
                        Realizar Pago Parcial
                      </Button>
                    </div>
                  );
                }}
              </FormDataConsumer>
            )}
            <TextField label="Metodo de Pago:"></TextField>
            {record && (
              <div onClick={(e) => e.stopPropagation()}>
                <PaymentStatusField record={record} />
              </div>
            )}
            <FunctionField
              label="Tiempo Estimado Total"
              render={({ totalEstimatedWorkMinutes }) => toHoursAndMinutes(totalEstimatedWorkMinutes)}
            />
            <TextField source="paymentInfo.status" label="Estado de Pago" />
            {record?.feedbackInfo?.rating && <TextField source="feedbackInfo.rating" label="Rating" />}
            {record?.feedbackInfo?.comment && <TextField source="feedbackInfo.comment" label="Comentario" />}
            {record && <BooleanField source="isInBranch" label="¿Servicio en Sucursal?" />}
            {record?.branch && <TextField source="branch.name" label="Sucursal" />}
            {record?.status === "Cancelada por el proveedor" && record?.cancelReason && (
              <TextField source="cancelReason" label="Motivo de cancelación" />
            )}

            <Box sx={{ "& button": { m: 1 } }}>
              {record?.employee?.fullName && <h4>Empleado asignado: {record.employee?.fullName || ""}</h4>}
              {record &&
                (record.status === "Pendiente" ||
                  record.status === "Pendiente de cotizacion" ||
                  record.status === "Confirmado") && <UseEmployee setEmployee={setEmployee} record={record} />}
              <br />
              {record &&
                (record.status === "Pendiente" ||
                  record.status === "Pendiente de cotizacion" ||
                  record.status === "Confirmado") && (
                  <Button variant="contained" color="primary" onClick={assignEmployee} size="small">
                    <>Asignar Empleado</>
                  </Button>
                )}
              <br />
              {record && record.status === "Pendiente" && (
                <Button variant="contained" color="primary" size="small" onClick={confirmWithoutEmployee}>
                  <>Aceptar reserva sin asignar empleado</>
                </Button>
              )}
              <br />
              {record &&
                (record.status === "Pendiente" ||
                  record.status === "Confirmado" ||
                  record.status === "Pendiente de cotizacion" ||
                  record.status === "Cotizacion enviada") &&
                record.createdBy === record.provider._id && (
                  <>
                    {/* <Button variant="contained" color="secondary" size="small" onClick={submitCancel}>
                      <>Cancelar Reserva</>
                    </Button> */}

                    <MuiButton variant="contained" color="secondary" size="small" onClick={handleOpenCancelModal}>
                      Cancelar Reserva
                    </MuiButton>
                    <br />
                  </>
                )}

              {record && record.status === "En proceso" && (
                <Button variant="contained" color="primary" size="small" onClick={submitFinish}>
                  Finalizar
                </Button>
              )}

              {record && record.status === "Confirmado" && record.employee?._id && (
                <Button variant="contained" color="primary" size="small" onClick={submitStart}>
                  Iniciar Servicio
                </Button>
              )}

              {record &&
                record.status === "Finalizado" &&
                record.paymentInfo.status === "Pendiente" &&
                record.employee?._id && (
                  <Button variant="contained" color="primary" size="small" onClick={submitPaid}>
                    Servicio Pagado
                  </Button>
                )}

              <br />
            </Box>
            {record?.notes && <TextField source="notes" label="Detalles" />}
          </Tab>
          <Tab label="Información del Cliente">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <h2 style={{ textAlign: "center" }}>{record?.customer.fullName}</h2>
              </Grid>

              <Grid item xs={12} md={record?.directionDestination ? 6 : 12}>
                {!record?.customer.address && <p>No se ha encontrado dirección para este cliente</p>}
                {!record?.isInBranch && record?.customer.address && (
                  <>
                    <h3>Dirección del cliente</h3>
                    <div style={{ height: "50vh", width: "100%" }}>
                      <GoogleMapReact
                        bootstrapURLKeys={{
                          key: process.env.REACT_APP_GOOGLE_MAPS_KEY,
                        }}
                        defaultCenter={{
                          lat: record?.customer.address?.coordinates.latitude,
                          lng: record?.customer.address?.coordinates.longitude,
                        }}
                        defaultZoom={15}
                      >
                        <RoomIcon
                          lat={record?.customer.address?.coordinates?.latitude}
                          lng={record?.customer.address?.coordinates?.longitude}
                          style={{
                            fontSize: 40,
                            position: "relative",
                            top: -40,
                            left: -20,
                          }}
                        />
                      </GoogleMapReact>
                    </div>
                    <span className="MuiTypography-root MuiTypography-body2">
                      {record?.customer.address.name}
                      {record?.customer.address.street} #{record?.customer.address.house},{" "}
                      {record?.customer.address.reference}
                    </span>
                  </>
                )}
                {record?.billingInfo?.businessName && (
                  <div>
                    <h3>Datos de Facturación</h3>
                    <span className="MuiTypography-root MuiTypography-body2">
                      {record?.billingInfo.businessName}, NIT:
                      {record?.billingInfo.NIT}
                    </span>
                  </div>
                )}
              </Grid>
            </Grid>
            <br></br>
          </Tab>
          {(record?.status === "Confirmado" ||
            record?.status === "En proceso" ||
            record?.status === "Pendiente de cotizacion" ||
            record?.status === "Cotizacion enviada") && (
            <Tab label="Chat">
              <div style={{ display: "flex", height: 500 }}>
                <Chat orderId={record._id} orderType="booking" userId={record.provider._id} />
              </div>
            </Tab>
          )}
        </TabbedShowLayout>
      </SimpleForm>
      {/* Modal de cancelación con motivo */}
      <Dialog open={openCancelModal} onClose={handleCloseCancelModal}>
        <DialogTitle>¿Por qué estás cancelando esta reserva?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Por favor, indica la razón. Esto ayudará al cliente a entender lo sucedido.
          </Typography>
          <MuiTextField
            autoFocus
            margin="dense"
            label="Motivo de cancelación *"
            type="text"
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            required
            placeholder="Ej: Cliente no pudo asistir, servicio ya no necesario..."
            error={!cancelReason.trim()}
            helperText={!cancelReason.trim() ? "La razón es obligatoria" : ""}
            data-cy="input-cancel-reason"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MuiButton onClick={handleCloseCancelModal} color="secondary">
            Volver
          </MuiButton>
          <MuiButton
            onClick={cancel}
            color="error"
            variant="contained"
            disabled={!cancelReason.trim()}
            data-cy="btn-confirm-cancel"
          >
            Confirmar Cancelación
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Edit>
    // </StyledBaseShow>
  );
};
